import { baseApi } from "./api";
import {
	clearProjectDetails,
	mergeProjectDetails,
	setProjectDetails,
} from "../slices/projectDetailsSlice";

export const projectsApi = baseApi.injectEndpoints({
	endpoints: (build) => ({
		getProjects: build.query({
			query: ({
				page = 1,
				limit = 50,
				search,
				status = "default",
			} = {}) => {
				const params = { limit, offset: (page - 1) * limit };
				if (search) params.search = search;
				if (status) params.status = status;
				return { url: "projects", params };
			},
			serializeQueryArgs: ({ queryArgs }) => {
				const { search, status } = queryArgs;
				return { search, status };
			},
			merge: (currentCache, incoming, { arg }) => {
				if (arg.page === 1 || !currentCache) return incoming;
				return {
					...incoming,
					items: [
						...(currentCache.items || []),
						...(incoming.items || []),
					],
				};
			},
			forceRefetch: ({ currentArg, previousArg }) => {
				if (!previousArg) return true;
				return (
					currentArg?.page !== previousArg?.page ||
					currentArg?.search !== previousArg?.search ||
					currentArg?.status !== previousArg?.status
				);
			},
			providesTags: (result) => [
				{ type: "Project", id: "LIST" },
				...((result?.items ?? []).map((p) => ({
					type: "Project",
					id: p.id,
				}))),
			],
		}),
		getProjectById: build.query({
			query: (projectId) => ({ url: `projects/${projectId}` }),
			providesTags: (_r, _e, projectId) => [
				{ type: "Project", id: projectId },
			],
			// Mirror data into projectDetailsSlice so selectCurrentProjectId
			// and selectProjectDetails (used in 30+ files) keep working.
			async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
				try {
					const { data } = await queryFulfilled;
					dispatch(setProjectDetails(data));
				} catch {
					// Errors handled by component via useGetProjectByIdQuery.
				}
			},
		}),
		createProject: build.mutation({
			query: (body) => ({
				url: "projects",
				method: "POST",
				body,
			}),
			invalidatesTags: [{ type: "Project", id: "LIST" }],
		}),
		updateProject: build.mutation({
			query: ({ id, ...body }) => ({
				url: `projects/${id}`,
				method: "PUT",
				body,
			}),
			invalidatesTags: (_r, _e, { id }) => [
				{ type: "Project", id },
				{ type: "Project", id: "LIST" },
			],
		}),
		deleteProject: build.mutation({
			query: (projectId) => ({
				url: `projects/${projectId}`,
				method: "DELETE",
			}),
			invalidatesTags: (_r, _e, projectId) => [
				{ type: "Project", id: projectId },
				{ type: "Project", id: "LIST" },
			],
		}),
		updateProjectSettings: build.mutation({
			query: ({ projectId, settings }) => ({
				url: `projects/${projectId}/settings`,
				method: "PUT",
				body: settings,
			}),
			invalidatesTags: (_r, _e, { projectId }) => [
				{ type: "Project", id: projectId },
			],
			// Merge the response into projectDetailsSlice so selectors update
			// without a full refetch.
			async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
				try {
					const { data } = await queryFulfilled;
					dispatch(mergeProjectDetails(data));
				} catch {
					/* nothing */
				}
			},
		}),
	}),
});

export const {
	useGetProjectsQuery,
	useGetProjectByIdQuery,
	useLazyGetProjectByIdQuery,
	useCreateProjectMutation,
	useUpdateProjectMutation,
	useDeleteProjectMutation,
	useUpdateProjectSettingsMutation,
} = projectsApi;

export { clearProjectDetails };
